import { NoAccounts } from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Container,
  Chip,
  Stack
} from "@mui/material";
import React from "react";
import { List } from "../../../@types/list";
import {
  AnsweringClient,
  IUserResponse,
  RoleTranslations,
} from "../../../clients/answeringClient";
import { PromoteClientModal } from "../../../ResellerClientsManagement/components/modals/PromoteClientModal";
import EmptyState from "../../../SharedComponents/EmptyState/EmptyState";
import { Pagination } from "../../../SharedComponents/Pagination";
import { SearchInput } from "../../../SharedComponents/SearchInput";
import { withNavigationHooks } from "../../../SharedComponents/withNavigationHooks";
import { calculateTotalPages } from "../../../utils/pagination";
import {
  PromoteToResellerModal,
  PromoteToResellerModalDelegate,
} from "../modals/PromoteToResellerModal/PromoteToResellerModal";
import AllUsersRow from "./AllUsersRow";
import { AllUsersController, AllUsersControllerDelegate } from "./controller";

interface State {
  isLoading: boolean;
  users: IUserResponse[];
  filters: {
    filter: string;
    page: number;
    limit: number;
    total: number;
  };

  userToPromote?: { uid: string; name: string };

  isPromoteModalOpen: boolean;
  isPromotionLoading: boolean;

  isPromotePsychologistModalOpen: boolean;
  isPromotePsychologistLoading: boolean;

  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: "success" | "error" | "info" | "warning";
  roleTranslations: RoleTranslations;
}

class AllUsersTab
  extends React.Component<{}, State>
  implements AllUsersControllerDelegate, PromoteToResellerModalDelegate {
  private controller: AllUsersController;
  private answeringClient = new AnsweringClient();
  constructor(props: {}) {
    super(props);
    this.controller = new AllUsersController(this);
    this.state = {
      isLoading: true,
      users: [],
      filters: { filter: "", page: 1, limit: 10, total: 0 },
      userToPromote: undefined,
      isPromoteModalOpen: false,
      isPromotionLoading: false,
      isPromotePsychologistModalOpen: false,
      isPromotePsychologistLoading: false,
      snackbarOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "success",
      roleTranslations: {},
    };
  }

  componentDidMount() {
    this.loadRoleTranslations();
    this.loadUsers();
  }

  private loadRoleTranslations = async () => {
    try {
      const token = localStorage.getItem("AccessToken");
      if (!token) return;
      const resp = await this.answeringClient.getRoleTranslations(token);
      this.setState({ roleTranslations: resp.data || {} });
    } catch (error) {
      console.warn(
        "Falha ao carregar traduções de roles. Usando fallback.",
        error
      );
      this.setState({ roleTranslations: {} });
    }
  };

  didGetAllUsers = (response: List<IUserResponse>) => {
    this.setState({
      isLoading: false,
      users: response.data,
      filters: {
        ...this.state.filters,
        page: response.page,
        total: response.total,
      },
    });
  };

  genericError = (message: string) => {
    this.setState({
      isLoading: false,
      isPromotionLoading: false,
      isPromotePsychologistLoading: false,
      snackbarOpen: true,
      snackbarMessage: message,
      snackbarSeverity: "error",
    });
  };

  loadUsers = () => {
    this.setState({ isLoading: true });
    const token = localStorage.getItem("AccessToken")!;
    const { page, limit, filter } = this.state.filters;
    this.controller.searchUsers(token, { page, limit, filter });
  };

  handleSearch = () => {
    this.setState(
      (prevState) => ({
        filters: { ...prevState.filters, page: 1 },
      }),
      this.loadUsers
    );
  };

  handleFilterChange = (newFilter: string) => {
    this.setState((prevState) => ({
      filters: { ...prevState.filters, filter: newFilter },
    }));
  };

  handlePageChange = (newPage: number) => {
    this.setState(
      (prevState) => {
        const totalPages =
          calculateTotalPages(prevState.filters.total, prevState.filters.limit) ||
          1;
        const page = Math.min(Math.max(newPage, 1), totalPages);
        if (page === prevState.filters.page) {
          return null;
        }
        return { filters: { ...prevState.filters, page } };
      },
      this.loadUsers
    );
  };

  handleSnackbarClose = () => {
    this.setState({ snackbarOpen: false });
  };

  /* RESELLER PROMOTION */
  handleOpenPromoteModal = (uid: string, name: string) => {
    this.setState({ isPromoteModalOpen: true, userToPromote: { uid, name } });
  };

  onConfirm = () => {
    this.setState({ isPromotionLoading: true });
    const token = localStorage.getItem("AccessToken")!;
    const uid = this.state.userToPromote!.uid;

    this.answeringClient
      .promoteToReseller(token, uid)
      .then(() => {
        this.setState(
          {
            isPromotionLoading: false,
            isPromoteModalOpen: false,
            snackbarOpen: true,
            snackbarMessage: "Usuário promovido a revendedor com sucesso!",
            snackbarSeverity: "success",
          },
          () => {
            this.loadUsers();
          }
        );
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || "Falha ao promover o usuário.";
        this.genericError(errorMessage);
      });
  };

  onClose = () => {
    this.setState({
      isPromoteModalOpen: false,
      userToPromote: undefined,
      isPromotionLoading: false,
    });
  };

  /* PSYCHOLOGIST PROMOTION */
  handleOpenPromotePsychologistModal = (uid: string, name: string) =>
    this.setState({
      isPromotePsychologistModalOpen: true,
      userToPromote: { uid, name },
    });
  onClosePromotion = () =>
    this.setState({
      isPromotePsychologistModalOpen: false,
      userToPromote: undefined,
    });
  onConfirmPromotion = async (cpf: string, crp: string) => {
    this.setState({ isPromotePsychologistLoading: true });
    const token = localStorage.getItem("AccessToken")!;
    try {
      await this.answeringClient.promoteToPsychologist(
        token,
        this.state.userToPromote!.uid,
        {
          cpf,
          crp,
        }
      );
      this.setState(
        {
          isPromotePsychologistLoading: false,
          isPromotePsychologistModalOpen: false,
          snackbarOpen: true,
          snackbarMessage: "Usuário promovido a psicólogo com sucesso.",
          snackbarSeverity: "success",
        }
        //this.loadClients
      );
    } catch (err: any) {
      this.genericError(
        err?.response?.data?.message || "Erro ao promover cliente."
      );
    }
  };

  render() {
    const {
      isLoading,
      users,
      filters,
      isPromoteModalOpen,
      isPromotionLoading,
      isPromotePsychologistLoading,
      isPromotePsychologistModalOpen,
      userToPromote,
      snackbarOpen,
      snackbarMessage,
      snackbarSeverity,
      roleTranslations,
    } = this.state;
    const totalPages = calculateTotalPages(filters.total, filters.limit);

    if (isLoading && users.length === 0) {
      return (
        <Box display="flex" justifyContent="center" sx={{ p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box>
        <Box flex={1} overflow="auto">
          <Container maxWidth="lg"
            style={{
              padding: "40px 20px",
            }}
          >
            <Box
              marginBottom={2}
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                gap: '0.75rem',
                alignItems: {
                  sm: 'center'
                },
                justifyContent: {
                  xs: 'space-between',
                },
                flexWrap: 'wrap'
              }}
            >
              <Stack direction={"column"} spacing={1.25}>
                <Box display="flex" alignItems="center" gap={1} flexWrap={'wrap'}>
                  <Typography variant="h2">
                    Todos os Usuários da Plataforma
                  </Typography>

                  <Chip
                    label={`${this.state.filters?.total} ${this.state.filters?.total !== 1 ? "encontrados" : "encontrado"}`}
                    size="small"
                  />
                </Box>

                <Typography variant="subtitle1" color={'text.secondary'}>
                  Controle e visualize a participação de todos os usuários
                </Typography>
              </Stack>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                gap: '0.75rem',
                alignItems: {
                  sm: 'center'
                },
                justifyContent: {
                  xs: 'start',
                  md: 'end'
                },
                flexWrap: 'wrap'
              }}
            >
              <SearchInput
                label="Pesquisar por nome ou email..."
                onChangeFilter={this.handleFilterChange}
                onSearch={this.handleSearch}
              />
            </Box>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table aria-label="all-users-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Papéis</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading && users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <EmptyState
                          icon={NoAccounts}
                          title="Nenhum usuário encontrado"
                          description="Tente ajustar os filtros ou adicionar novos usuários."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                  {!isLoading &&
                    users.map((user) => (
                      <AllUsersRow
                        key={user.uid}
                        user={user}
                        roleTranslations={roleTranslations}
                        onPromoteClick={this.handleOpenPromoteModal}
                        onPromotePsychologist={
                          this.handleOpenPromotePsychologistModal
                        }
                      />
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Pagination
              currentPage={filters.page}
              totalPages={totalPages}
              onChangePage={this.handlePageChange}
            />
          </Container>
        </Box>

        {isPromoteModalOpen && (
          <PromoteToResellerModal
            isOpen={isPromoteModalOpen}
            isLoading={isPromotionLoading}
            userName={userToPromote?.name ?? ""}
            delegate={this}
          />
        )}
        {isPromotePsychologistModalOpen && (
          <PromoteClientModal
            isOpen={isPromotePsychologistModalOpen}
            isLoading={isPromotePsychologistLoading}
            clientName={userToPromote?.name ?? ""}
            delegate={this}
            isToClient={false}
          />
        )}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={this.handleSnackbarClose}
        >
          <Alert
            onClose={this.handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

export default withNavigationHooks(AllUsersTab);
